import React, { useCallback, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Card, ListItem, ListItemText } from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Container from "@mui/material/Container";
import List from "@mui/material/List";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import AddUserRoleDialog from "./AddUserRoleDialog";
import { useDeleteUserRole } from "./roleServices";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 400,
    padding: theme.spacing(3),
    margin: theme.spacing(1),
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },

  newButton: {
    textTransform: "uppercase",
    color: "#127DA9",
    fontSize: "1.2rem",
    fontWeight: "bold",
  },

  listItem: {
    borderBottomColor: theme.palette.primary.dark,
    borderBottomStyle: "dashed",
    borderBottomWidth: 1,
  },
}));

const UserRolesCard = ({ user, onAlert }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);

  const deleteUserRole = useDeleteUserRole();
  const handleDeleteRole = useCallback(
    async roleName => {
      try {
        await deleteUserRole.mutateAsync({
          resourceIds: {
            id: user.userId,
            role: roleName,
          },
        });
      } catch (err) {
        onAlert("error", [err.message]);
      }
    },
    [deleteUserRole, user, onAlert],
  );

  const handleAddSuccess = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleAddError = useCallback(
    error => {
      onAlert("error", [error.message]);
    },
    [onAlert],
  );

  return (
    <Card classes={{ root: classes.root }}>
      <AddUserRoleDialog
        open={dialogOpen}
        onCancel={useCallback(() => setDialogOpen(false), [])}
        onOk={handleAddSuccess}
        onError={handleAddError}
        user={user}
      />
      <Box className={classes.topBar}>
        <Box style={{ marginBottom: "10px" }}>
          <Typography variant="h2" color="primary" noWrap>
            User Roles
          </Typography>
        </Box>

        <Grid container spacing={3} direction="row" justifyContent="flex-end">
          <Grid item>
            <Button
              variant={"text"}
              startIcon={<AddIcon />}
              classes={{ root: classes.newButton }}
              onClick={useCallback(() => setDialogOpen(true), [])}
            >
              Add User Role
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Container maxWidth={false}>
        <List dense={true}>
          {user.roles.map(role => (
            <ListItem classes={{ root: classes.listItem }} key={role.roleId}>
              <ListItemText primary={role.roleName} />
              <ListItemSecondaryAction>
                <ConfirmButton
                  handleDelete={() => handleDeleteRole(role.roleName)}
                  dialogTitle="Remove Role"
                  dialogBody="Are you sure you want to remove this role?"
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Container>
    </Card>
  );
};

export default UserRolesCard;
